import { type SortingState, type Updater } from "@tanstack/react-table";
import { type TFunction } from "i18next";
import { ArrowDownUp } from "lucide-react";

import { WFPButton } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import { COMMODITY_SORT_OPTIONS } from "./List";

const SORTING_MENU_ITEM_CLASS =
  "data-[state=checked]:bg-primary-50 data-[state=checked]:font-medium data-[state=checked]:text-primary-700 focus:bg-primary-100 focus:text-primary-700";

interface CommoditiesSortMenuProps {
  sorting: SortingState;
  onSortingChange: (updater: Updater<SortingState>) => void;
  t: TFunction;
}

// Sort dropdown mirroring the aggregators list: it drives the same TanStack sorting state as
// the column headers, so picking a column/direction here or clicking a header stay in sync.
export const CommoditiesSortMenu = ({ sorting, onSortingChange, t }: CommoditiesSortMenuProps) => {
  const selectedSort = sorting[0];
  const selectedSortOption = COMMODITY_SORT_OPTIONS.find(option => option.id === selectedSort?.id);
  const selectedSortColumn = selectedSortOption?.id ?? "";
  const selectedSortDirection = selectedSort?.desc ? "desc" : "asc";
  const sortButtonLabel = selectedSortOption
    ? t(selectedSortOption.translationKey)
    : t("commodities.list.sorting.button");

  const onSortColumnChange = (sortColumn: string) =>
    onSortingChange([{ id: sortColumn, desc: selectedSortDirection === "desc" }]);

  const onSortDirectionChange = (sortDirection: string) =>
    onSortingChange([
      {
        id: selectedSortColumn || COMMODITY_SORT_OPTIONS[0].id,
        desc: sortDirection === "desc",
      },
    ]);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <WFPButton type="button" variant="outline">
          <ArrowDownUp className="size-4" aria-hidden="true" />
          {sortButtonLabel}
        </WFPButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-56">
        <DropdownMenuLabel>{t("commodities.list.sorting.sortBy")}</DropdownMenuLabel>
        <DropdownMenuRadioGroup value={selectedSortColumn} onValueChange={onSortColumnChange}>
          {COMMODITY_SORT_OPTIONS.map(sortOption => (
            <DropdownMenuRadioItem
              key={sortOption.id}
              value={sortOption.id}
              indicator="check"
              className={SORTING_MENU_ITEM_CLASS}
            >
              {t(sortOption.translationKey)}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
        <DropdownMenuSeparator />
        <DropdownMenuLabel>{t("commodities.list.sorting.order")}</DropdownMenuLabel>
        <DropdownMenuRadioGroup value={selectedSortDirection} onValueChange={onSortDirectionChange}>
          <DropdownMenuRadioItem value="asc" indicator="check" className={SORTING_MENU_ITEM_CLASS}>
            {t("commodities.list.sorting.ascending")}
          </DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="desc" indicator="check" className={SORTING_MENU_ITEM_CLASS}>
            {t("commodities.list.sorting.descending")}
          </DropdownMenuRadioItem>
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
